/**
 * Single bet card in the Recent Bets section of UserProfileModal.
 * Handles tail button state, AI analysis expand/collapse, and leg display.
 */

import { TierBadge } from '@components/ui/TierBadge';
import { getOutcomeColor } from '@utils/colorUtils';
import { AlertTriangle, Lock } from 'lucide-react';
import type { ReactElement } from 'react';

function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface BetLeg {
  player?: string | null;
  prop?: string | null;
  outcome: string;
}

interface RecentBet {
  id: string;
  outcome: string;
  tier?: string | null;
  legCount: number;
  name?: string | null;
  createdAt?: string | Date | null;
  settledAt: string | Date;
  totalOdds?: string | null;
  legs: BetLeg[];
  aiReasoning?: string | null;
}

interface FollowerPermissions {
  allowTailParlays?: boolean;
  showDetailedAnalysis?: boolean;
}

export interface RecentBetCardProps {
  bet: RecentBet;
  isFollowing: boolean;
  followerPermissions?: FollowerPermissions | null;
  isTailing: boolean;
  isAnalysisExpanded: boolean;
  canSeeAnalysis: boolean;
  followLocked: boolean;
  isOwnBet: boolean;
  onTail: (parlayId: string) => void;
  onToggleAnalysis: (betId: string) => void;
  onShowFollowUpgrade: () => void;
  onFollow: () => void;
  displayName: string | null;
}

export function RecentBetCard({
  bet,
  isFollowing,
  followerPermissions,
  isTailing,
  isAnalysisExpanded,
  canSeeAnalysis,
  followLocked,
  isOwnBet: _isOwnBet,
  onTail,
  onToggleAnalysis,
  onShowFollowUpgrade,
  onFollow,
  displayName,
}: RecentBetCardProps): ReactElement {
  const hasCompleteData = bet.legs.every((leg) => leg.player && leg.prop);
  const hasAiAnalysis = !!bet.aiReasoning;

  type ButtonState = 'active' | 'not-following' | 'no-permission' | 'incomplete-data';
  let buttonState: ButtonState = 'active';
  let buttonText = 'Tail';
  let buttonIcon: ReactElement | null = null;
  let buttonTooltip = '';

  if (!isFollowing) {
    buttonState = 'not-following';
    buttonText = 'Follow to tail';
    buttonIcon = <Lock className="w-3 h-3" />;
    buttonTooltip = 'Follow this user to tail their parlays';
  } else if (!followerPermissions?.allowTailParlays) {
    buttonState = 'no-permission';
    buttonText = "User hasn't shared tailing";
    buttonIcon = <Lock className="w-3 h-3" />;
    buttonTooltip = 'This user has not enabled parlay tailing for followers';
  } else if (!hasCompleteData) {
    buttonState = 'incomplete-data';
    buttonText = 'Missing bet details';
    buttonIcon = <AlertTriangle className="w-3 h-3" />;
    buttonTooltip = 'This parlay is missing some leg information';
  }

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${getOutcomeColor(bet.outcome)}`}
            >
              {bet.outcome.toUpperCase()}
            </span>
            {bet.tier && <span className="text-xs text-theme-text-tertiary">{bet.tier}</span>}
            <span className="text-xs text-theme-text-tertiary">{bet.legCount} legs</span>
          </div>
          {bet.name && <div className="text-sm text-theme-text-secondary mt-1">{bet.name}</div>}
        </div>
        <div className="text-right">
          <div className="text-xs text-theme-text-secondary">
            {formatDate(bet.createdAt ?? bet.settledAt)}
          </div>
          <div className="text-[10px] text-theme-text-tertiary mt-0.5">
            Settled {formatDate(bet.settledAt)}
          </div>
          {bet.totalOdds && (
            <div className="text-xs text-theme-text-tertiary mt-1">{bet.totalOdds}</div>
          )}
        </div>
      </div>

      {/* Leg list */}
      <div className="space-y-2 mb-3">
        {bet.legs.map((leg, i) => (
          <div
            key={i}
            className="text-sm text-theme-text-secondary flex items-center justify-between"
          >
            <span>
              {leg.player} {leg.prop}
            </span>
            <span
              className={`text-xs ${
                leg.outcome === 'won'
                  ? 'text-theme-success'
                  : leg.outcome === 'lost'
                    ? 'text-theme-danger'
                    : 'text-theme-text-secondary'
              }`}
            >
              {leg.outcome}
            </span>
          </div>
        ))}
      </div>

      {/* AI Analysis */}
      {hasAiAnalysis && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => onToggleAnalysis(bet.id)}
            disabled={!canSeeAnalysis}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              canSeeAnalysis
                ? 'bg-theme-surface-hover hover:bg-theme-surface-hover text-gray-200'
                : 'bg-theme-surface-hover/50 text-theme-text-tertiary cursor-not-allowed'
            }`}
            title={
              !canSeeAnalysis
                ? isFollowing
                  ? "User hasn't shared AI analysis"
                  : 'Follow to see AI analysis'
                : ''
            }
          >
            {canSeeAnalysis ? (
              <>
                <span>{isAnalysisExpanded ? '▼' : '▶'}</span>
                <span>{isAnalysisExpanded ? 'Hide' : 'View'} AI Analysis</span>
              </>
            ) : (
              <>
                <Lock className="w-3 h-3" />
                <span>
                  {isFollowing ? "User hasn't shared AI analysis" : 'Follow to see AI analysis'}
                </span>
              </>
            )}
          </button>

          {isAnalysisExpanded && canSeeAnalysis && (
            <div className="mt-2 p-3 bg-theme-surface/50 rounded-lg border border-theme-border">
              <div className="prose prose-sm prose-invert max-w-none">
                <div className="text-xs text-theme-text-secondary whitespace-pre-wrap">
                  {bet.aiReasoning}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tail button */}
      <button
        type="button"
        onClick={() => buttonState === 'active' && onTail(bet.id)}
        disabled={buttonState !== 'active' || isTailing}
        title={buttonTooltip}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          buttonState === 'active'
            ? 'bg-theme-primary-fill hover:bg-theme-primary-fill text-theme-on-primary-fill'
            : 'bg-theme-surface-hover/50 text-theme-text-secondary cursor-not-allowed'
        } ${isTailing ? 'opacity-50' : ''}`}
      >
        {isTailing ? (
          <>
            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            <span>Validating...</span>
          </>
        ) : (
          <>
            {buttonIcon}
            <span>{buttonText}</span>
          </>
        )}
      </button>

      {/* Locked follow prompt within tail area */}
      {!isFollowing && buttonState === 'not-following' && (
        <div className="mt-2 text-center">
          {followLocked ? (
            <button
              type="button"
              onClick={onShowFollowUpgrade}
              className="inline-flex items-center gap-1.5 text-xs text-theme-text-secondary hover:text-theme-text transition-colors"
            >
              <TierBadge feature="social_follow" size="sm" />
              Upgrade to follow
            </button>
          ) : (
            <button
              type="button"
              onClick={onFollow}
              className="text-xs text-theme-primary hover:underline"
            >
              Follow {displayName}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
